import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import DoctorHeader from '../components/doctor/DoctorHeader';
import { antrianService, clinicService, doctorDashboardService, tenantApi } from '../services/api';
import { useClinicDocumentMeta } from '../hooks/useClinicDocumentMeta';

const DEFAULT_ACCENT = '#06B6D4';

const DoctorDashboard = () => {
    const { slug } = useParams();

    const [doctorData, setDoctorData] = useState(null);
    const [clinicBrand, setClinicBrand] = useState(null);
    const [queues, setQueues] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filterType, setFilterType] = useState('umum');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [currentQueue, setCurrentQueue] = useState(null);
    const [queueStatus, setQueueStatus] = useState('dipanggil');
    const [successMessage, setSuccessMessage] = useState('');

    const accent = clinicBrand?.primary_color || DEFAULT_ACCENT;

    useClinicDocumentMeta({
        title: clinicBrand?.nama_klinik ? `${clinicBrand.nama_klinik} — Dashboard Dokter` : 'Dashboard Dokter',
        faviconUrl: clinicBrand?.logo_url || null,
        enabled: !!slug && !!clinicBrand,
    });

    const getPoliPrefix = (poliName) => {
        const name = (poliName || '').toLowerCase();
        if (name.includes('umum')) return 'A';
        if (name.includes('gigi')) return 'C';
        if (name.includes('anak')) return 'B';
        return name.charAt(0).toUpperCase() || 'Q';
    };

    const capitalize = (s) => {
        if (!s) return '';
        if (s === 'menunggu') return 'Menunggu';
        if (s === 'dipanggil') return 'Dilayani';
        if (s === 'selesai') return 'Selesai';
        if (s === 'batal') return 'Batal';
        return s.charAt(0).toUpperCase() + s.slice(1);
    };

    const toDateOnly = (value) => {
        if (!value) return '';
        if (typeof value === 'string') {
            // Handles "YYYY-MM-DD" and ISO datetime strings from API.
            const isoPart = value.split('T')[0];
            if (/^\d{4}-\d{2}-\d{2}$/.test(isoPart)) return isoPart;
        }
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return '';
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    const mapRows = (items) =>
        items.map((item) => {
            const prefix = getPoliPrefix(item.poli?.nama_poli);
            const indexPart = String(item.nomor_antrian).padStart(2, '0');
            const datePart = String(item.tanggal).split('-').reverse().join('');

            return {
                no: `${prefix}${indexPart}-${datePart}`,
                name: item.pasien
                    ? item.pasien.username || item.pasien.nama_pasien
                    : 'Pasien Tidak Diketahui',
                type: item.jenis_antrian === 'bpjs' ? 'Antrian BPJS' : 'Antrian Umum',
                clinic: item.poli ? item.poli.nama_poli : 'Poli Tidak Diketahui',
                time: (item.tanggal || '') + ' ' + (item.jam ? item.jam.substring(0, 5) : ''),
                status: capitalize(item.status_antrian),
                raw: item,
            };
        });

    const fetchQueues = useCallback(
        async (user, showLoading = true) => {
            try {
                if (showLoading) setIsLoading(true);

                let rows = [];

                if (slug) {
                    const response = await doctorDashboardService.getQueues(slug, {
                        jenis_antrian: filterType,
                        tanggal: selectedDate,
                    });
                    if (response.data?.success) {
                        rows = mapRows(response.data.data || []);
                    }
                } else {
                    const response = await antrianService.getAll();
                    if (response.data?.success) {
                        const doctorId = user.dokter?.id_dokter ?? user.id_dokter ?? user.id;
                        const filtered = (response.data.data || []).filter(
                            (q) =>
                                q.id_dokter === doctorId && q.jenis_antrian === filterType
                        );
                        rows = mapRows(filtered);
                    }
                }

                setQueues(rows);
            } catch (error) {
                console.error('Error fetching queues:', error);
            } finally {
                if (showLoading) setIsLoading(false);
            }
        },
        [slug, filterType, selectedDate]
    );

    useEffect(() => {
        if (!slug) {
            setClinicBrand(null);
            return;
        }
        clinicService
            .getBySlug(slug)
            .then((res) => {
                if (res.data?.success) setClinicBrand(res.data.data);
                else setClinicBrand(null);
            })
            .catch(() => setClinicBrand(null));
    }, [slug]);

    useEffect(() => {
        const userStr = localStorage.getItem('user');
        if (!userStr) return;

        const user = JSON.parse(userStr);
        setDoctorData(user);
        fetchQueues(user, true);

        const intervalId = setInterval(() => fetchQueues(user, false), 3000);
        return () => clearInterval(intervalId);
    }, [fetchQueues]);

    const today = new Date();
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const formattedDate = today.toLocaleDateString('id-ID', options);

    const activeQueues = useMemo(
        () =>
            queues.filter((q) => {
                return ['menunggu', 'dipanggil'].includes(q.raw.status_antrian);
            }),
        [queues]
    );

    const filteredQueues = useMemo(
        () =>
            activeQueues.filter((q) => {
                const matchesSearch =
                    q.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    q.no.toLowerCase().includes(searchQuery.toLowerCase());
                return matchesSearch;
            }),
        [activeQueues, searchQuery]
    );

    const todayQueueCount = useMemo(
        () => activeQueues.length,
        [activeQueues],
    );

    const handleEditClick = (queue) => {
        setCurrentQueue(queue);
        let s = queue.raw.status_antrian;
        if (s !== 'dipanggil' && s !== 'selesai') {
            s = 'dipanggil';
        }
        setQueueStatus(s);
        setIsEditModalOpen(true);
    };

    const handleUpdateStatus = async (e) => {
        e.preventDefault();
        try {
            if (slug) {
                await tenantApi(slug).antrian.update(currentQueue.raw.id_antrian, {
                    status_antrian: queueStatus,
                });
            } else {
                await antrianService.update(currentQueue.raw.id_antrian, { status_antrian: queueStatus });
            }

            if (queueStatus === 'selesai') {
                // Optimistic sync: remove completed queue immediately from active list.
                setQueues((prev) =>
                    prev.filter((q) => q.raw.id_antrian !== currentQueue.raw.id_antrian)
                );
                setSuccessMessage('Antrian berhasil diselesaikan.');
            } else {
                const updatedQueues = queues.map((q) => {
                    if (q.raw.id_antrian === currentQueue.raw.id_antrian) {
                        return {
                            ...q,
                            status: capitalize(queueStatus),
                            raw: { ...q.raw, status_antrian: queueStatus },
                        };
                    }
                    return q;
                });
                setQueues(updatedQueues);
                setSuccessMessage('Status antrian berhasil diperbarui.');
            }
            setIsEditModalOpen(false);
            // Re-sync lightweight from server so UI and DB stay consistent.
            fetchQueues(doctorData, false);
            setTimeout(() => setSuccessMessage(''), 1800);
        } catch (error) {
            console.error('Gagal mengupdate status antrian:', error);
            alert('Gagal mengupdate status. Silakan coba lagi.');
        }
    };

    const tabActiveStyle = {
        backgroundColor: accent,
        color: '#fff',
        boxShadow: `0 10px 15px -3px ${accent}40`,
    };

    return (
        <div
            className="min-h-screen bg-white font-jakarta pb-20"
            style={{ '--clinic-accent': accent }}
        >
            <DoctorHeader doctorData={doctorData} brand={clinicBrand} primaryColor={accent} />

            <main className="max-w-7xl mx-auto pt-32 px-10">
                {successMessage && (
                    <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
                        {successMessage}
                    </div>
                )}
                <div className="mb-10">
                    <h1 className="text-4xl font-bold tracking-tight">
                        Selamat Datang,{' '}
                        <span style={{ color: accent }}>
                            {doctorData?.name || doctorData?.dokter?.nama_dokter || 'Dokter'}
                        </span>
                    </h1>
                    <p className="text-gray-400 mt-2 font-medium">
                        {doctorData?.dokter?.nama_dokter || 'Dokter'} — {formattedDate}
                        {clinicBrand?.nama_klinik ? (
                            <span className="block text-sm mt-1 text-gray-500">
                                {clinicBrand.nama_klinik}
                            </span>
                        ) : null}
                    </p>
                </div>

                <div
                    className="flex items-center gap-8 border rounded-2xl p-6 bg-white shadow-sm mb-12"
                    style={{ borderColor: `${accent}33` }}
                >
                    <div className="flex items-center gap-3 pr-8 border-r border-gray-100">
                        <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white"
                            style={{ backgroundColor: accent }}
                        >
                            <Icon icon="material-symbols:check" className="text-xl" />
                        </div>
                        <span className="text-gray-600 font-bold">
                            Antrian hari ini :{' '}
                            <span className="text-gray-400 font-medium ml-1">{todayQueueCount} pasien</span>
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-gray-600 font-bold">
                            Jam Praktik :{' '}
                            <span className="text-gray-400 font-medium ml-1">
                                {doctorData?.dokter?.jadwal || '08:00 - 12:00'}
                            </span>
                        </span>
                    </div>
                </div>

                <div className="flex gap-4 mb-8">
                    <button
                        type="button"
                        onClick={() => setFilterType('umum')}
                        className={`px-8 py-3 rounded-2xl font-bold text-sm transition-all ${
                            filterType === 'umum' ? '' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                        }`}
                        style={filterType === 'umum' ? tabActiveStyle : undefined}
                    >
                        Antrian Umum
                    </button>
                    <button
                        type="button"
                        onClick={() => setFilterType('bpjs')}
                        className={`px-8 py-3 rounded-2xl font-bold text-sm transition-all ${
                            filterType === 'bpjs' ? '' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                        }`}
                        style={filterType === 'bpjs' ? tabActiveStyle : undefined}
                    >
                        Antrian BPJS
                    </button>
                </div>

                <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 font-jakarta">
                        Daftar Antrian {filterType === 'bpjs' ? 'BPJS' : 'Umum'}
                    </h2>

                    <div className="flex flex-col sm:flex-row gap-4 mb-8">
                        <div className="relative flex-1">
                            <Icon
                                icon="material-symbols:search"
                                className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-300 text-2xl"
                            />
                            <input
                                type="text"
                                placeholder="Cari nama atau nomor antrian"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-16 pr-6 py-4 bg-white border border-gray-100 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/10 transition-all shadow-sm"
                            />
                        </div>
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="px-6 py-4 bg-white border border-gray-100 rounded-2xl text-sm xl:min-w-[200px] focus:outline-none transition-all shadow-sm text-gray-600"
                        />
                    </div>

                    <div className="bg-white border border-gray-50 rounded-3xl overflow-hidden shadow-sm">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-gray-50/50 border-b border-gray-50">
                                    <th className="px-10 py-6 text-left text-sm font-bold text-gray-700">
                                        Nomor Antrian
                                    </th>
                                    <th className="px-10 py-6 text-left text-sm font-bold text-gray-700">
                                        Nama Pasien
                                    </th>
                                    <th className="px-10 py-6 text-left text-sm font-bold text-gray-700">Jenis</th>
                                    <th className="px-10 py-6 text-left text-sm font-bold text-gray-700">
                                        Tanggal & Jam
                                    </th>
                                    <th className="px-10 py-6 text-left text-sm font-bold text-gray-700">Status</th>
                                    <th className="px-10 py-6 text-left text-sm font-bold text-gray-700">Aksi</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50/50">
                                {isLoading ? (
                                    <tr>
                                        <td
                                            colSpan={6}
                                            className="px-10 py-10 text-center text-gray-400 font-medium"
                                        >
                                            Memuat antrian...
                                        </td>
                                    </tr>
                                ) : filteredQueues.length > 0 ? (
                                    filteredQueues.map((item, index) => (
                                        <tr key={index} className="hover:bg-gray-50/30 transition-colors">
                                            <td className="px-10 py-5">
                                                <div
                                                    className="w-fit px-8 py-2 text-white font-bold rounded-2xl text-sm shadow-lg"
                                                    style={{
                                                        background: `linear-gradient(90deg, ${accent}, ${accent}cc)`,
                                                        boxShadow: `0 10px 15px -3px ${accent}33`,
                                                    }}
                                                >
                                                    {item.no}
                                                </div>
                                            </td>
                                            <td className="px-10 py-5">
                                                <span className="text-gray-700 font-bold text-sm tracking-tight">
                                                    {item.name}
                                                </span>
                                            </td>
                                            <td className="px-10 py-5">
                                                <span className="text-gray-500 font-medium text-sm">
                                                    {item.type.replace('Antrian ', '')}
                                                </span>
                                            </td>
                                            <td className="px-10 py-5">
                                                <div className="flex flex-col">
                                                    <span className="text-gray-700 font-bold text-[13px]">
                                                        {item.raw.tanggal}
                                                    </span>
                                                    <span className="text-gray-400 text-[11px] font-medium">
                                                        {item.raw.jam}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-10 py-5">
                                                <div
                                                    className={`flex items-center gap-2 px-4 py-1.5 text-[11px] font-bold rounded-lg w-fit ${
                                                        item.raw.status_antrian === 'selesai'
                                                            ? 'bg-[#E8F8EA] text-[#0DBC94]'
                                                            : item.raw.status_antrian === 'dipanggil'
                                                              ? 'bg-[#FFF4E5] text-[#FF9800]'
                                                              : 'bg-[#E1F7FC]'
                                                    }`}
                                                    style={
                                                        item.raw.status_antrian === 'menunggu'
                                                            ? { color: accent }
                                                            : undefined
                                                    }
                                                >
                                                    <div
                                                        className={`w-2 h-2 rounded-full ${
                                                            item.raw.status_antrian === 'selesai'
                                                                ? 'bg-[#0DBC94]'
                                                                : item.raw.status_antrian === 'dipanggil'
                                                                  ? 'bg-[#FF9800]'
                                                                  : ''
                                                        }`}
                                                        style={
                                                            item.raw.status_antrian === 'menunggu'
                                                                ? { backgroundColor: accent }
                                                                : undefined
                                                        }
                                                    />
                                                    <span>{item.status}</span>
                                                </div>
                                            </td>
                                            <td className="px-10 py-5">
                                                <button
                                                    type="button"
                                                    onClick={() => handleEditClick(item)}
                                                    className="p-2 text-gray-400 rounded-lg transition-colors hover:bg-[#E0F7FA]"
                                                    style={{ ['--hover-c']: accent }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.color = accent;
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.color = '';
                                                    }}
                                                    title="Ubah Status Antrian"
                                                >
                                                    <Icon icon="material-symbols:edit-outline" className="text-xl" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={6}
                                            className="px-10 py-10 text-center text-gray-400 font-medium italic"
                                        >
                                            No patients found for today.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>

            {isEditModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-3xl p-8 w-full max-w-md shadow-2xl animate-fade-in">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-gray-800">Ubah Status Antrian</h3>
                            <button
                                type="button"
                                onClick={() => setIsEditModalOpen(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <Icon icon="material-symbols:close" className="text-2xl" />
                            </button>
                        </div>
                        <div className="mb-4">
                            <p className="text-sm font-medium text-gray-500">
                                Nomor:{' '}
                                <span className="font-bold text-gray-800">{currentQueue?.no}</span>
                            </p>
                            <p className="text-sm font-medium text-gray-500">
                                Pasien:{' '}
                                <span className="font-bold text-gray-800">{currentQueue?.name}</span>
                            </p>
                        </div>
                        <form onSubmit={handleUpdateStatus} className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">
                                    Pilih Status Baru
                                </label>
                                <select
                                    value={queueStatus}
                                    onChange={(e) => setQueueStatus(e.target.value)}
                                    className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none transition-all"
                                >
                                    <option value="dipanggil">Dilayani</option>
                                    <option value="selesai">Selesai</option>
                                </select>
                            </div>
                            <button
                                type="submit"
                                className="w-full py-3 text-white font-bold rounded-xl transition-colors mt-4"
                                style={{ backgroundColor: accent }}
                            >
                                Simpan Perubahan
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DoctorDashboard;
